import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { clearSelectedMarker, clearSelectedMarkerIds, selectIsAddingMapLocation, selectIsBulkMovingLocations, selectIsBulkSelectingPins, selectIsInCreatorMode, selectIsMovingLocation, selectMapData, selectSelectedMarkerId, selectSelectedMarkerIds, setIsBulkSelectingPins, setSelectedMarkerIds, toggleMarkerInSelection } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { findClosestPoint } from '@repo/types/maps/map-utils';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PIN_INTERACTION_LAYER_IDS } from '../../game-map/hooks/constants';
import { useMapContext } from '../../game-map/map-context';
import { DraggableFloatingBar } from './draggable-floating-bar';
const getIsCustomPin = (feature: {
  properties?: {
    isCustomPin?: unknown;
  };
}) => feature.properties?.isCustomPin === true || feature.properties?.isCustomPin === 'true';
const getFeatureExternalId = (feature: {
  properties?: {
    externalId?: unknown;
  };
}) => typeof feature.properties?.externalId === 'string' ? feature.properties.externalId : null;

/** Panel button that turns selection mode on. */
export const BulkSelectMapPinsAction = () => {
  const $ = _c(10);
  const dispatch = useAppDispatch();
  const mapData = useAppSelector(selectMapData);
  const isAddingMapLocation = useAppSelector(selectIsAddingMapLocation);
  const isBulkMovingLocations = useAppSelector(selectIsBulkMovingLocations);
  const isBulkSelectingPins = useAppSelector(selectIsBulkSelectingPins);
  const isMovingLocation = useAppSelector(selectIsMovingLocation);
  const selectedMarkerIds = useAppSelector(selectSelectedMarkerIds);
  let t0;
  if ($[0] !== mapData?.geojson.features) {
    t0 = mapData?.geojson.features.filter(_temp).length ?? 0;
    $[0] = mapData?.geojson.features;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const officialPinCount = t0;
  let t1;
  if ($[2] !== dispatch) {
    t1 = () => {
      dispatch(clearSelectedMarker());
      dispatch(setIsBulkSelectingPins(true));
      toast({
        title: "Select pins",
        description: "Click pins to add or remove them from the selection.",
        variant: "info"
      });
    };
    $[2] = dispatch;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleEnter = t1;
  const t2 = isBulkSelectingPins || isBulkMovingLocations || isAddingMapLocation || isMovingLocation || officialPinCount === 0;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = css({
      flex: "1",
      whiteSpace: "nowrap"
    });
    t4 = <Icon icon="check_box" decorative={true} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = selectedMarkerIds.length > 0 ? "Change selection" : "Select pins on the map";
  let t6;
  if ($[6] !== handleEnter || $[7] !== t2 || $[8] !== t5) {
    t6 = <Button onClick={handleEnter} color="secondary" size="sm" disabled={t2} className={t3}>{t4}{t5}</Button>;
    $[6] = handleEnter;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

/** Floating bar plus the map click handling, active only while selecting. */
export const BulkSelectMapPinsControls = () => {
  const $ = _c(51);
  const dispatch = useAppDispatch();
  const {
    map
  } = useMapContext();
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const isBulkSelectingPins = useAppSelector(selectIsBulkSelectingPins);
  const selectedMarkerIds = useAppSelector(selectSelectedMarkerIds);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const mapData = useAppSelector(selectMapData);
  const mapDataRef = useRef(mapData);
  let t0;
  let t1;
  if ($[0] !== mapData) {
    t0 = () => {
      mapDataRef.current = mapData;
    };
    t1 = [mapData];
    $[0] = mapData;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== mapData?.geojson.features) {
    t2 = mapData?.geojson.features ?? [];
    $[3] = mapData?.geojson.features;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = t2.filter(_temp2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const officialFeatures = t3;
  let t4;
  let t5;
  if ($[7] !== map || $[8] !== officialFeatures || $[9] !== selectedMarkerId || $[10] !== selectedMarkerIds) {
    t4 = () => {
      if (!map) {
        return;
      }
      let source;
      try {
        source = map.getSource("selected-marker") as MapLibre.GeoJSONSource | undefined;
      } catch {
        return;
      }
      if (!source) {
        return;
      }
      const chosen = new Set(selectedMarkerIds);
      const asHighlight = _temp3;
      const features = officialFeatures.filter(feature_1 => chosen.has(feature_1.properties.externalId)).map(asHighlight);
      if (selectedMarkerId && !chosen.has(selectedMarkerId)) {
        const openPin = (mapDataRef.current?.geojson.features ?? []).find(feature_2 => feature_2.properties.externalId === selectedMarkerId);
        if (openPin) {
          features.push(asHighlight(openPin));
        }
      }
      source.setData({
        type: "FeatureCollection",
        features
      } as GeoJSON.FeatureCollection);
    };
    t5 = [map, selectedMarkerIds, selectedMarkerId, officialFeatures];
    $[7] = map;
    $[8] = officialFeatures;
    $[9] = selectedMarkerId;
    $[10] = selectedMarkerIds;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== dispatch) {
    t6 = () => {
      dispatch(setIsBulkSelectingPins(false));
    };
    $[13] = dispatch;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleDone = t6;
  let t7;
  let t8;
  if ($[15] !== dispatch || $[16] !== isBulkSelectingPins || $[17] !== map) {
    t7 = () => {
      if (!isBulkSelectingPins || !map) {
        return;
      }
      const targetMap = map;
      let markerLayers;
      try {
        markerLayers = PIN_INTERACTION_LAYER_IDS.filter(layer => targetMap.getLayer(layer));
      } catch {
        return;
      }
      if (markerLayers.length === 0) {
        return;
      }
      const handleClick = e => {
        const officialPoints = e.features?.filter(_temp4) ?? [];
        const closest = findClosestPoint(officialPoints, e.lngLat);
        const externalId = closest ? getFeatureExternalId(closest) : null;
        if (!externalId) {
          return;
        }
        e.preventDefault();
        dispatch(toggleMarkerInSelection(externalId));
      };
      targetMap.on("click", markerLayers, handleClick);
      return () => {
        targetMap.off("click", markerLayers, handleClick);
      };
    };
    t8 = [dispatch, isBulkSelectingPins, map];
    $[15] = dispatch;
    $[16] = isBulkSelectingPins;
    $[17] = map;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  if (!isInCreatorMode || !isBulkSelectingPins) {
    return null;
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon icon="drag_indicator" size="sm" decorative={true} className={css({
      color: "whiteAlpha.40"
    })} />;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = selectedMarkerIds.length === 1 ? "" : "s";
  let t11;
  if ($[21] !== selectedMarkerIds.length || $[22] !== t10) {
    t11 = <Type variant="header-xxs" color="white">{selectedMarkerIds.length} pin{t10} selected</Type>;
    $[21] = selectedMarkerIds.length;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t9) {
    t12 = <FlexRow gap="4" align="center">{t9}{t11}</FlexRow>;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== officialFeatures.length) {
    t13 = <Type variant="body-xs" color="text.secondary">of {officialFeatures.length} on this map</Type>;
    $[27] = officialFeatures.length;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <FlexRow gap="8" align="center" justify="between">{t12}{t13}</FlexRow>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Type variant="body-xs" color="text.secondary">Click a pin to add it, click again to remove it. Drag this bar to move it out of the way.</Type>;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== dispatch || $[34] !== officialFeatures) {
    t16 = <Button variant="ghost" onClick={() => dispatch(setSelectedMarkerIds(officialFeatures.map(_temp5)))} color="secondary" size="sm">Select all</Button>;
    $[33] = dispatch;
    $[34] = officialFeatures;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== dispatch) {
    t17 = () => dispatch(clearSelectedMarkerIds());
    $[36] = dispatch;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = selectedMarkerIds.length === 0;
  let t19;
  if ($[38] !== t17 || $[39] !== t18) {
    t19 = <Button variant="ghost" onClick={t17} color="secondary" size="sm" disabled={t18}>Clear</Button>;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Icon icon="check" decorative={true} />;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== handleDone) {
    t21 = <Button onClick={handleDone} color="success" size="sm">{t20}Done</Button>;
    $[42] = handleDone;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t16 || $[45] !== t19 || $[46] !== t21) {
    t22 = <FlexRow gap="8" justify="end">{t16}{t19}{t21}</FlexRow>;
    $[44] = t16;
    $[45] = t19;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t14 || $[49] !== t22) {
    t23 = <DraggableFloatingBar><FlexColumn gap="10">{t14}{t15}{t22}</FlexColumn></DraggableFloatingBar>;
    $[48] = t14;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
};
function _temp(feature) {
  return !feature.properties.isCustomPin;
}
function _temp2(feature) {
  return !feature.properties.isCustomPin;
}
function _temp3(feature_0) {
  return {
    ...feature_0,
    id: feature_0.properties.externalId,
    properties: {
      ...feature_0.properties,
      selected: true
    }
  };
}
function _temp4(feature_3) {
  return feature_3.geometry.type === "Point" && !getIsCustomPin(feature_3);
}
function _temp5(f) {
  return f.properties.externalId;
}